"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

import { LastCTA } from "@/app/_components/last-cta";
import { AdSenseResponsive } from "@/app/_components/adsense-responsive";
import { AdSenseScript } from "@/app/_components/adsense-script";

import { cn } from "@/lib/utils";

interface Props {
  children: React.ReactNode;
}

const intro =
  "/guias/guia-pratico-para-leilao-de-imoveis-passo-a-passo-simplificado";
const capitulo1 =
  "/guias/guia-pratico-para-leilao-de-imoveis-passo-a-passo-simplificado/o-que-e-um-leilao-de-imoveis";
const capitulo2 =
  "/guias/guia-pratico-para-leilao-de-imoveis-passo-a-passo-simplificado/como-encontrar-oportunidades-de-leilao";
const capitulo3 =
  "/guias/guia-pratico-para-leilao-de-imoveis-passo-a-passo-simplificado/analise-e-planejamento";
const capitulo4 =
  "/guias/guia-pratico-para-leilao-de-imoveis-passo-a-passo-simplificado/participando-do-leilao";
const capitulo5 =
  "/guias/guia-pratico-para-leilao-de-imoveis-passo-a-passo-simplificado/pos-arrematacao-e-regularizacao";
const capitulo6 =
  "/guias/guia-pratico-para-leilao-de-imoveis-passo-a-passo-simplificado/rentabilizando-o-imovel";
const capitulo7 =
  "/guias/guia-pratico-para-leilao-de-imoveis-passo-a-passo-simplificado/conclusao";

const downloadEbook =
  "/guias/guia-pratico-para-leilao-de-imoveis-passo-a-passo-simplificado/baixar-ebook";

export default function GuideLayout({ children }: Props) {
  const pathname = usePathname();

  return (
    <>
      <AdSenseScript />
      <div className="flex flex-col items-center justify-center gap-28 py-20 px-5 sm:px-9 mx-auto my-0 w-full sm:w-[1200px] print:w-auto print:p-0">
        <div className="flex flex-row gap-12 items-center justify-center print:hidden">
          <img
            loading="eager"
            src="/guides/guia-pratico-para-leilao-de-imoveis-passo-a-passo-simplificado.png"
            alt="Guia Prático para Leilão de Imóveis: Passo a Passo Simplificado"
            width={135}
            className="shadow-lg rounded-sm -rotate-6"
          />
          <div className="flex flex-col gap-2">
            <h1 className="text-3xl sm:text-4xl font-bold text-left">
              Guia Prático para Leilão de Imóveis:
            </h1>
            <h2 className="text-3xl sm:text-4xl font-bold text-left">
              Passo a Passo Simplificado
            </h2>
          </div>
        </div>
        <div
          className={cn(
            "flex flex-col sm:flex-row gap-24 print:gap-0",
            pathname === downloadEbook || pathname === intro ? "gap-0" : ""
          )}
        >
          {pathname !== downloadEbook ? (
            <ul
              className={cn(
                "list-none flex flex-col gap-4 print:hidden",
                pathname === intro ? "text-center gap-6" : null
              )}
            >
              <li>
                <Link
                  prefetch={false}
                  className={cn(
                    "text-sm whitespace-nowrap flex flex-col gap-1",
                    pathname === capitulo1
                      ? "font-bold border-l-2 border-primary p-2"
                      : ""
                  )}
                  href={capitulo1}
                >
                  Capítulo 1{" "}
                  <span className="text-muted-foreground font-normal">
                    O Que é um Leilão de Imóveis?
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  prefetch={false}
                  className={cn(
                    "text-sm whitespace-nowrap flex flex-col gap-1",
                    pathname === capitulo2
                      ? "font-bold border-l-2 border-primary p-2"
                      : ""
                  )}
                  href={capitulo2}
                >
                  Capítulo 2{" "}
                  <span className="text-muted-foreground font-normal">
                    Como Encontrar Oportunidades de Leilão
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  prefetch={false}
                  className={cn(
                    "text-sm whitespace-nowrap flex flex-col gap-1",
                    pathname === capitulo3
                      ? "font-bold border-l-2 border-primary p-2"
                      : ""
                  )}
                  href={capitulo3}
                >
                  Capítulo 3{" "}
                  <span className="text-muted-foreground font-normal">
                    Análise e Planejamento
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  prefetch={false}
                  className={cn(
                    "text-sm whitespace-nowrap flex flex-col gap-1",
                    pathname === capitulo4
                      ? "font-bold border-l-2 border-primary p-2"
                      : ""
                  )}
                  href={capitulo4}
                >
                  Capítulo 4{" "}
                  <span className="text-muted-foreground font-normal">
                    Participando do Leilão
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  prefetch={false}
                  className={cn(
                    "text-sm whitespace-nowrap flex flex-col gap-1",
                    pathname === capitulo5
                      ? "font-bold border-l-2 border-primary p-2"
                      : ""
                  )}
                  href={capitulo5}
                >
                  Capítulo 5{" "}
                  <span className="text-muted-foreground font-normal">
                    Pós-Arrematação e Regularização
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  prefetch={false}
                  className={cn(
                    "text-sm whitespace-nowrap flex flex-col gap-1",
                    pathname === capitulo6
                      ? "font-bold border-l-2 border-primary p-2"
                      : ""
                  )}
                  href={capitulo6}
                >
                  Capítulo 6{" "}
                  <span className="text-muted-foreground font-normal">
                    Rentabilizando o Imóvel
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  prefetch={false}
                  className={cn(
                    "text-sm whitespace-nowrap flex flex-col gap-1",
                    pathname === capitulo7
                      ? "font-bold border-l-2 border-primary p-2"
                      : ""
                  )}
                  href={capitulo7}
                >
                  Capítulo 7{" "}
                  <span className="text-muted-foreground font-normal">
                    Conclusão
                  </span>
                </Link>
              </li>
            </ul>
          ) : null}
          <div>{children}</div>
        </div>
        <AdSenseResponsive orientation="horizontal" variant="guide" />
        <div className="my-20 print:hidden">
          <LastCTA />
        </div>
      </div>
    </>
  );
}
